import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { 
  BuildingOffice2Icon, 
  PlusIcon, 
  ArrowRightIcon,
  MapPinIcon,
  TrashIcon
} from '@heroicons/react/24/solid';

interface PropertyGroup {
  id: string;
  name: string;
  slug: string;
  description?: string;
  _count: {
    addresses: number;
  };
  createdAt: string;
}

const PropertyGroupSelection: React.FC = () => {
  const [propertyGroups, setPropertyGroups] = useState<PropertyGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user } = useAuth();

  useEffect(() => {
    fetchPropertyGroups();
  }, []);

  const fetchPropertyGroups = async () => {
    try {
      setLoading(true);
      const response = await api.get<PropertyGroup[]>('/api/property-groups');
      setPropertyGroups(response.data);
    } catch (err: any) {
      console.error('Failed to fetch property groups:', err);
      setError('Failed to load property groups');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectGroup = (groupId: string, groupSlug: string) => {
    localStorage.setItem('selectedPropertyGroup', groupId);
    localStorage.setItem('selectedPropertyGroupSlug', groupSlug);
    navigate(`/${groupSlug}/dashboard`);
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    
    try {
      setCreating(true);
      const slug = newGroupName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      
      const response = await api.post<PropertyGroup>('/api/property-groups', {
        name: newGroupName.trim(),
        slug,
        description: newGroupDescription.trim() || undefined
      });
      
      // Auto-select the newly created group
      handleSelectGroup(response.data.id, response.data.slug || slug);
    } catch (err: any) {
      console.error('Failed to create property group:', err);
      setError(err.response?.data?.message || 'Failed to create property group');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteGroup = async (e: React.MouseEvent, groupId: string, groupName: string) => {
    e.stopPropagation(); // Prevent card click
    
    if (!window.confirm(`Are you sure you want to delete "${groupName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/api/property-groups/${groupId}`);
      setError(''); // Clear any previous errors
      await fetchPropertyGroups(); // Refresh the list
    } catch (err: any) {
      console.error('Failed to delete property group:', err);
      setError(err.response?.data?.message || 'Failed to delete property group');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-900 flex items-center justify-center">
        <div className="text-white">Loading Property Groups...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-r from-primary to-secondary flex items-center justify-center shadow-futuristic">
            <BuildingOffice2Icon className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Select Property Group
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Choose which property group you'd like to manage, or create a new one to organize your properties.
          </p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-8 max-w-md mx-auto">
            <p className="text-red-400 text-center">{error}</p>
          </div>
        )}

        {/* Property Groups Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {propertyGroups.map((group) => (
            <div
              key={group.id}
              onClick={() => handleSelectGroup(group.id, group.slug)}
              className="glass-card p-6 cursor-pointer group hover:scale-105 transition-all duration-300 relative"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-primary to-secondary flex items-center justify-center">
                  <MapPinIcon className="w-6 h-6 text-white" />
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => handleDeleteGroup(e, group.id, group.name)}
                    className="p-2 rounded-lg hover:bg-red-500/20 transition-colors opacity-0 group-hover:opacity-100"
                    title="Delete group"
                  >
                    <TrashIcon className="w-4 h-4 text-white/60 hover:text-red-400" />
                  </button>
                  <ArrowRightIcon className="w-5 h-5 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-white mb-2">{group.name}</h3>
              {group.description && (
                <p className="text-white/60 text-sm mb-4">{group.description}</p>
              )}
              
              <div className="flex items-center justify-between">
                <span className="text-white/70 text-sm">
                  {group._count.addresses} {group._count.addresses === 1 ? 'Address' : 'Addresses'}
                </span>
                <span className="text-primary text-sm font-medium">
                  Select →
                </span>
              </div>
            </div>
          ))}

          {/* Create New Group Card */}
          <div
            onClick={() => setShowCreateForm(true)}
            className="glass-card p-6 cursor-pointer group hover:scale-105 transition-all duration-300 border-2 border-dashed border-white/20 hover:border-primary/50"
          >
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-r from-primary/20 to-secondary/20 flex items-center justify-center group-hover:from-primary group-hover:to-secondary transition-all duration-300">
                <PlusIcon className="w-6 h-6 text-white/60 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Create New Group</h3>
              <p className="text-white/60 text-sm">
                Add a new property group to organize your addresses
              </p>
            </div>
          </div>
        </div>

        {/* Create Group Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="glass-card p-8 max-w-md w-full mx-4">
              <h3 className="text-2xl font-bold text-white mb-6">Create Property Group</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Group Name *
                  </label>
                  <input
                    type="text"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="e.g., Downtown Properties, West Side Apartments"
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={newGroupDescription}
                    onChange={(e) => setNewGroupDescription(e.target.value)}
                    placeholder="Brief description of this property group"
                    rows={3}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none resize-none"
                  />
                </div>
              </div>
              
              <div className="flex space-x-3 mt-8">
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewGroupName('');
                    setNewGroupDescription('');
                    setError('');
                  }}
                  className="flex-1 px-4 py-3 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateGroup}
                  disabled={!newGroupName.trim() || creating}
                  className="flex-1 btn-primary"
                >
                  {creating ? 'Creating...' : 'Create Group'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {propertyGroups.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 mx-auto mb-8 rounded-3xl bg-gradient-to-r from-primary/20 to-secondary/20 flex items-center justify-center">
              <BuildingOffice2Icon className="w-12 h-12 text-white/60" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">No Property Groups Yet</h3>
            <p className="text-white/60 mb-8 max-w-md mx-auto">
              Create your first property group to start organizing your properties by location, type, or any system that works for you.
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="btn-primary"
            >
              Create Your First Group
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyGroupSelection;